import type { WheelPickerOption } from "@ncdai/react-wheel-picker";
import { WheelPicker, WheelPickerWrapper } from "@ncdai/react-wheel-picker";
import type { ComponentProps } from "react";
import "@ncdai/react-wheel-picker/dist/style.css";

import { LockIcon } from "../icons";
import { cn } from "../lib/cn";

const DEFAULT_API_TIME = "07:00:00";
const DEFAULT_PICKER_TIME = {
  timeRange: "07:00 - 07:30",
  meridiem: "AM",
} as const;
const API_TIME_PATTERN = /^(\d{2}):(\d{2}):(\d{2})$/;
const DISPLAY_TIME_PATTERN = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
const TIME_RANGE_PATTERN = /^(\d{2}):(\d{2})\s*-\s*(\d{2}):(\d{2})$/;

const MERIDIEM_OPTIONS: Array<WheelPickerOption<string>> = [
  { label: "AM", value: "AM" },
  { label: "PM", value: "PM" },
];

type Meridiem = (typeof MERIDIEM_OPTIONS)[number]["value"];
type PickerTimeValue = {
  timeRange: string;
  meridiem: Meridiem;
};

function convert24HourTo12Hour(hour24: number): number {
  if (hour24 === 0) {
    return 12;
  }
  if (hour24 > 12) {
    return hour24 - 12;
  }
  return hour24;
}

function convert12HourTo24Hour(hour12: number, meridiem: string): number {
  const upperMeridiem = meridiem.toUpperCase();
  if (upperMeridiem === "PM" && hour12 !== 12) {
    return hour12 + 12;
  }
  if (upperMeridiem === "AM" && hour12 === 12) {
    return 0;
  }
  return hour12;
}

function formatHour(hour: number): string {
  return hour.toString().padStart(2, "0");
}

function createTimeRange(hour: number, startMinute: 0 | 30): string {
  const nextHour = hour === 12 ? 1 : hour + 1;
  if (startMinute === 0) {
    return `${formatHour(hour)}:00 - ${formatHour(hour)}:30`;
  }
  return `${formatHour(hour)}:30 - ${formatHour(nextHour)}:00`;
}

const TIME_RANGE_OPTIONS: Array<WheelPickerOption<string>> = Array.from(
  { length: 12 },
  (_, index) => index + 1,
).flatMap((hour) => {
  const firstHalf = createTimeRange(hour, 0);
  const secondHalf = createTimeRange(hour, 30);

  return [
    { label: firstHalf, value: firstHalf },
    { label: secondHalf, value: secondHalf },
  ];
});

function getApiMatch(apiTime: string) {
  return apiTime.match(API_TIME_PATTERN);
}

function getTimeRangeMatch(timeRange: string) {
  return timeRange.match(TIME_RANGE_PATTERN);
}

function isMeridiem(value: string): value is Meridiem {
  return MERIDIEM_OPTIONS.some((option) => option.value === value);
}

function normalizeToApiFormat(value: string): string {
  if (getApiMatch(value)) {
    return value;
  }

  const match = value.match(DISPLAY_TIME_PATTERN);
  if (!match?.[1] || !match[2] || !match[3]) {
    return DEFAULT_API_TIME;
  }

  const hour12 = Number.parseInt(match[1], 10);
  const minute = match[2];
  const meridiem = match[3].toUpperCase();
  const hour24 = convert12HourTo24Hour(hour12, meridiem);

  return `${formatHour(hour24)}:${minute}:00`;
}

function convertApiFormatToPickerFormat(apiTime: string): PickerTimeValue {
  const match = getApiMatch(apiTime);
  if (!match?.[1] || !match[2]) {
    return DEFAULT_PICKER_TIME;
  }

  const hour24 = Number.parseInt(match[1], 10);
  const minute = Number.parseInt(match[2], 10);
  const meridiem: Meridiem = hour24 >= 12 ? "PM" : "AM";
  const hour12 = convert24HourTo12Hour(hour24);
  const startMinute = minute === 30 ? 30 : 0;

  return {
    timeRange: createTimeRange(hour12, startMinute),
    meridiem,
  };
}

export function convertPickerFormatToApiFormat(
  timeRange: string,
  meridiem: string,
): string {
  const match = getTimeRangeMatch(timeRange);
  if (!match?.[1] || !match[2]) {
    return DEFAULT_API_TIME;
  }

  const normalizedMeridiem = meridiem.toUpperCase();
  if (!isMeridiem(normalizedMeridiem)) {
    return DEFAULT_API_TIME;
  }

  const hour12 = Number.parseInt(match[1], 10);
  const startMinute = match[2];
  const hour24 = convert12HourTo24Hour(hour12, normalizedMeridiem);

  return `${formatHour(hour24)}:${startMinute}:00`;
}

export interface TimeWheelPickerProps
  extends Omit<ComponentProps<"fieldset">, "onChange" | "value"> {
  value?: string;
  onChange?: (time: string) => void;
  readOnly?: boolean;
  isFreeUser?: boolean;
  visibleCount?: number;
  optionItemHeight?: number;
}

export function TimeWheelPicker({
  value = "07:00:00",
  onChange,
  readOnly = false,
  isFreeUser = false,
  visibleCount = 12,
  optionItemHeight = 40,
  className,
  ...fieldsetProps
}: TimeWheelPickerProps) {
  const pickerValue = convertApiFormatToPickerFormat(
    normalizeToApiFormat(value),
  );
  const isBlocked = readOnly || isFreeUser;
  const blockedInteractionClassName = isBlocked
    ? "pointer-events-none select-none touch-none"
    : undefined;
  const emitChange = (nextValue: PickerTimeValue) => {
    if (isBlocked || !onChange) return;
    onChange(
      convertPickerFormatToApiFormat(nextValue.timeRange, nextValue.meridiem),
    );
  };

  return (
    <fieldset
      {...fieldsetProps}
      className={cn(
        "relative flex flex-row",
        isFreeUser ? "w-[210px]" : "w-[200px]",
        className,
      )}
      aria-label={fieldsetProps["aria-label"] ?? "Time selection wheel picker"}
      aria-disabled={isBlocked || undefined}
    >
      <WheelPickerWrapper
        className={cn(
          "time-picker relative justify-end",
          isFreeUser ? "!w-[150px]" : "!w-[140px]",
          blockedInteractionClassName,
        )}
        data-testid="time-wheel-picker-time-range-wrapper"
      >
        <WheelPicker
          options={TIME_RANGE_OPTIONS}
          value={pickerValue.timeRange}
          onValueChange={
            isBlocked
              ? undefined
              : (timeRange) => {
                  emitChange({ timeRange, meridiem: pickerValue.meridiem });
                }
          }
          infinite
          visibleCount={visibleCount}
          optionItemHeight={optionItemHeight}
          aria-label="Select time range"
          data-testid="time-wheel-picker-time-range"
          classNames={{
            highlightWrapper:
              "border border-twi-gray-900 bg-[#181818] rounded-l-md border-r-0 relative",
            highlightItem: cn(isFreeUser ? "text-twi-gray-500" : "text-white"),
            optionItem: isFreeUser ? "text-twi-gray-500" : "text-white",
          }}
        />
        {isFreeUser ? (
          <div className="pointer-events-none absolute top-1/2 left-4 z-10 -translate-x-1/2 -translate-y-1/2">
            <LockIcon className="h-4 w-4" aria-hidden="true" />
          </div>
        ) : null}
      </WheelPickerWrapper>
      <WheelPickerWrapper
        data-testid="time-wheel-picker-meridiem-wrapper"
        className={cn("meridiem-picker !w-[60px]", blockedInteractionClassName)}
      >
        <WheelPicker
          options={MERIDIEM_OPTIONS}
          value={pickerValue.meridiem}
          onValueChange={
            isBlocked
              ? undefined
              : (meridiem) => {
                  emitChange({
                    timeRange: pickerValue.timeRange,
                    meridiem: meridiem as Meridiem,
                  });
                }
          }
          visibleCount={visibleCount}
          optionItemHeight={optionItemHeight}
          aria-label="Select AM or PM"
          data-testid="time-wheel-picker-meridiem"
          classNames={{
            highlightWrapper:
              "border border-twi-gray-900 bg-[#181818] rounded-r-md border-l-0 relative",
            optionItem: isFreeUser ? "text-twi-gray-500" : "text-white",
            highlightItem: cn(isFreeUser ? "text-twi-gray-500" : "text-white"),
          }}
        />
      </WheelPickerWrapper>
      {isBlocked ? (
        <div
          className="absolute inset-0 z-10 cursor-not-allowed touch-none"
          aria-hidden="true"
        />
      ) : null}
    </fieldset>
  );
}
